import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function ImpersonationApprove() {
  const { t } = useLang();
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);

  async function load() {
    try {
      const res = await fetch(`${API}/impersonation/request/${token}`);
      if (!res.ok) throw new Error(t("pub.requestNotFound"));
      setState({ loading: false, data: await res.json(), error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message });
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  async function approve() {
    setApproving(true);
    try {
      const res = await fetch(`${API}/impersonation/approve/${token}`, { method: "POST" });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || t("pub.errorWord")); }
      setApproved(true);
    } catch (e) {
      setState((s) => ({ ...s, error: e.message }));
    } finally { setApproving(false); }
  }

  const d = state.data;
  const isPending = d && d.status === "pending";

  return (
    <div className="min-h-screen bg-findo-navy flex items-center justify-center p-6" data-testid="impersonation-approve">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl">
        <Logo size={34} withText />
        {state.loading ? (
          <div className="mt-8 flex items-center gap-2 text-slate-500"><FindoSpinner size={18} /> {t("c.loading")}</div>
        ) : approved ? (
          <div className="mt-8 text-center" data-testid="impersonation-approved">
            <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
            <h1 className="mt-4 font-display text-2xl font-bold text-findo-navy">{t("pub.accessGranted")}</h1>
            <p className="mt-2 text-slate-600">{t("pub.impGrantedDesc")}</p>
          </div>
        ) : state.error || !d ? (
          <div className="mt-8 text-center" data-testid="impersonation-error">
            <XCircle className="mx-auto text-findo-danger" size={48} />
            <h1 className="mt-4 font-display text-2xl font-bold text-findo-navy">{t("pub.requestUnavailable")}</h1>
            <p className="mt-2 text-slate-600">{state.error || t("pub.requestGone")}</p>
          </div>
        ) : !isPending ? (
          <div className="mt-8 text-center">
            <ShieldCheck className="mx-auto text-slate-400" size={48} />
            <h1 className="mt-4 font-display text-2xl font-bold text-findo-navy">{t("pub.requestHandled")}</h1>
            <p className="mt-2 text-slate-600">{t("pub.statusLabel")} {d.status}. {t("pub.noActionRequired")}</p>
          </div>
        ) : (
          <div className="mt-8">
            <ShieldCheck className="text-findo-indigo" size={40} />
            <h1 className="mt-4 font-display text-2xl font-bold text-findo-navy">{t("pub.authorizeSupportQ")}</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
              {t("pub.impAdminReq1")} <b>{d.admin_email}</b> {t("pub.impAdminReq2")}{d.target_name ? <> (<b>{d.target_name}</b>)</> : null} {t("pub.impAdminReq3")}
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-slate-500">
              <li>• {t("pub.accessLimited2h")}</li>
              <li>• {t("pub.allActionsLogged")}</li>
              <li>• {t("pub.revokeAnytime")}</li>
            </ul>
            <button onClick={approve} disabled={approving}
              className="mt-6 w-full rounded-full bg-findo-navy text-white font-semibold py-3 hover:bg-findo-navy/90 transition-colors disabled:opacity-60"
              data-testid="impersonation-approve-btn">
              {approving ? "…" : t("pub.authorizeAccess")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
